import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import { Check, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { identifyUser, track } from "@/lib/posthog";
import logo from "@/assets/huelo-logo.svg";

const emailSchema = z.string().trim().email().max(255);
const nameSchema = z.string().trim().max(120);

const WaitlistPage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Get Huelo updates";
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta?.getAttribute("content") ?? "";
    meta?.setAttribute(
      "content",
      "Get product updates and early access to new Huelo features and higher design limits.",
    );
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute("content", prevDesc);
    };
  }, []);

  const showError = (message: string) =>
    toast({ title: "Something went wrong", description: message, variant: "destructive" });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailSchema.safeParse(email).success) return showError("Enter a valid email.");
    const trimmedName = name.trim();
    if (trimmedName && !nameSchema.safeParse(trimmedName).success) {
      return showError("Name must be 120 characters or fewer.");
    }

    setLoading(true);
    const normalizedEmail = email.trim().toLowerCase();
    const { error } = await supabase.from("waitlist_signups").insert({
      email: normalizedEmail,
      name: trimmedName || null,
      source: "waitlist-page",
    });
    setLoading(false);
    if (error) return showError(error.message);

    // Identify the waitlist signup in PostHog by email (no auth user yet),
    // then capture a dedicated event so we can funnel waitlist → activation.
    try {
      identifyUser(normalizedEmail, {
        email: normalizedEmail,
        name: trimmedName || undefined,
        waitlist_source: "waitlist-page",
        waitlist_signed_up_at: new Date().toISOString(),
      });
      track("waitlist_signup", {
        email: normalizedEmail,
        has_name: !!trimmedName,
        source: "waitlist-page",
      });
    } catch (err) {
      console.warn("posthog waitlist track failed", err);
    }


    setSubmitted(true);
  };

  return (
    <div className="min-h-[100dvh] bg-background flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <a href="https://huelo.co" aria-label="Go to huelo.co" rel="noopener noreferrer">
            <img src={logo} alt="Huelo" className="h-10 w-auto mb-3" />
          </a>
          <h1 className="font-display text-2xl md:text-4xl tracking-tight text-foreground">Huelo</h1>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
          {submitted ? (
            <div className="text-center space-y-4 py-4">
              <div className="mx-auto h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                <Check className="h-6 w-6 text-foreground" />
              </div>
              <h2 className="font-display text-xl text-foreground">You're on the list</h2>
              <p className="text-sm text-muted-foreground">
                We'll be in touch with product updates and early access to new features.
              </p>
              <Link
                to="/"
                className="inline-block text-sm text-foreground underline underline-offset-4 hover:opacity-80"
              >
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <h2 className="font-display text-xl text-foreground">Stay in the loop</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  Get product updates and early access to higher design limits and new features. You can start using Huelo right away — no invite needed.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="waitlist-name">Name (optional)</Label>
                  <Input
                    id="waitlist-name"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    maxLength={120}
                    disabled={loading}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="waitlist-email">Email</Label>
                  <Input
                    id="waitlist-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={255}
                    disabled={loading}
                    required
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={loading} aria-describedby="waitlist-consent">
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Sign me up
                </Button>
                <p id="waitlist-consent" className="text-xs text-muted-foreground text-center leading-relaxed">
                  By signing up, you agree to our{" "}
                  <Link to="/privacy" className="underline underline-offset-2 hover:text-foreground">
                    Privacy Policy
                  </Link>
                  . We'll email you about early access, product updates, and occasional invitations to share feedback.
                </p>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Ready to start?{" "}
          <Link to="/" className="text-foreground underline underline-offset-4 hover:opacity-80">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
};

export default WaitlistPage;
